import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { AudiobookState, Manuscript } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useContextMenu } from "../components/ContextMenu.js";
import { formatNumber } from "../lib/format.js";
import { AudiobookPlayer } from "./AudiobookPlayer.js";

const VOICE_KEY = "clovascribe:ttsVoice";
// Rough narration pace for estimating un-rendered chapter length (words/minute).
const WORDS_PER_MIN = 195;

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${s}s`;
}
function estMsFromWords(words: number): number {
  return Math.round((words / WORDS_PER_MIN) * 60000);
}
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const sameDay = d.toDateString() === now.toDateString();
  const yd = new Date(now); yd.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yd.toDateString();
  if (sameDay) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

type Filter = "all" | "rendered" | "pending" | "not";
type ChapterStatus = "rendered" | "rendering" | "pending" | "not";

/**
 * Audiobook Mode: render the whole book, or a chapter at a time, to narrated
 * audio in the background. Cache-aware per scene, so edits only re-render what
 * changed.
 */
export function AudiobookPanel({ projectId, manuscript }: { projectId: string; manuscript: Manuscript }) {
  const toast = useToast();
  const { open: openMenu, menu } = useContextMenu();
  const [state, setState] = useState<AudiobookState | null>(null);
  const [voices, setVoices] = useState<string[]>([]);
  const [voice, setVoice] = useState<string>("");
  const [available, setAvailable] = useState<boolean | null>(null);
  const [player, setPlayer] = useState<{ scope: "book" | "chapter"; chapterId?: string; title: string } | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try { setState(await api.audiobook(projectId)); } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => {
    load();
    api.ttsStatus(projectId)
      .then((s) => {
        setAvailable(s.available);
        setVoices(s.voices);
        let stored = "";
        try { stored = localStorage.getItem(VOICE_KEY) ?? ""; } catch { /* ignore */ }
        setVoice(stored && s.voices.includes(stored) ? stored : s.defaultVoice || s.voices[0] || "");
      })
      .catch(() => setAvailable(false));
  }, [load, projectId]);

  const render = state?.render;
  const rendering = state?.rendering || render?.status === "rendering";

  useEffect(() => {
    if (rendering && !pollRef.current) pollRef.current = setInterval(load, 2000);
    else if (!rendering && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [rendering, load]);

  useEffect(() => () => { previewRef.current?.pause(); }, []);

  const rememberVoice = () => { if (voice) try { localStorage.setItem(VOICE_KEY, voice); } catch { /* ignore */ } };

  const start = async (scope: "book" | "chapter", chapterId?: string) => {
    rememberVoice();
    try { setState(await api.renderAudiobook(projectId, { voice: voice || undefined, scope, chapterId })); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Could not start render"); }
  };
  const cancel = async () => {
    try { await api.cancelAudiobook(projectId); toast.info("Stopping after the current clip…"); load(); } catch { /* ignore */ }
  };
  const clearAll = async () => {
    if (rendering) { toast.info("Stop the current render first."); return; }
    const anyRendered = (state?.outputs ?? []).length > 0;
    if (!anyRendered) { toast.info("There are no renders to delete."); return; }
    if (!window.confirm("Delete ALL rendered audio for this book? Every chapter will need to be re-rendered. This can't be undone.")) return;
    try {
      const r = await api.clearAudiobook(projectId);
      toast.success(`Deleted all renders (${r.removed} file${r.removed === 1 ? "" : "s"} removed).`);
      setPlayer(null);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Could not delete renders");
    }
  };
  const download = async (scope: "book" | "chapter", chapterId?: string) => {
    try { await api.downloadAudiobook(projectId, { scope, chapterId, voice: voice || undefined }); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Download failed"); }
  };

  const previewVoice = () => {
    if (previewing) { previewRef.current?.pause(); setPreviewing(false); return; }
    // Preview by playing the start of any already-rendered chapter in this voice.
    const sample = (state?.outputs ?? []).find((o) => o.scope === "chapter" && (!voice || o.voice === voice));
    if (!sample) { toast.info("Render a chapter first to preview this narrator's voice."); return; }
    const url = api.audiobookAudioUrl(projectId, { scope: "chapter", chapterId: sample.scopeId, voice: voice || undefined });
    const audio = new Audio(url);
    previewRef.current = audio;
    audio.onended = () => setPreviewing(false);
    audio.play().then(() => setPreviewing(true)).catch(() => { setPreviewing(false); toast.error("Couldn't play a preview."); });
    window.setTimeout(() => { if (previewRef.current === audio) { audio.pause(); setPreviewing(false); } }, 12000);
  };

  // Outputs for the currently selected voice, by scope key.
  const outputByKey = useMemo(() => {
    const map = new Map<string, { durationMs: number; createdAt: string }>();
    for (const o of state?.outputs ?? []) {
      if (!voice || o.voice === voice) map.set(`${o.scope}:${o.scopeId}`, { durationMs: o.durationMs, createdAt: o.createdAt });
    }
    return map;
  }, [state?.outputs, voice]);

  const bookDuration = outputByKey.get("book:")?.durationMs;
  const total = render?.total ?? 0;
  const done = render?.done ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const activeChapter = render?.scope === "chapter" ? render.scopeId : null;
  const bookRendering = rendering && render?.scope === "book";
  // During a whole-book render the active chapter isn't an id on the render row;
  // derive it from the live label ("Chapter title · Scene" / "Rendered …").
  const activeChapterTitle = bookRendering && render?.currentLabel && !render.currentLabel.startsWith("Rendered ")
    ? render.currentLabel.split(" · ")[0]
    : null;

  const chapterPos = useMemo(() => new Map(manuscript.chapters.map((c) => [c.id, c.position])), [manuscript.chapters]);
  const chapterWords = useMemo(() => new Map(manuscript.chapters.map((c) => [c.id, c.wordCount])), [manuscript.chapters]);

  const chapters = useMemo(() => {
    const list = [...(state?.chapters ?? [])];
    list.sort((a, b) => (chapterPos.get(a.id) ?? 0) - (chapterPos.get(b.id) ?? 0));
    return list.map((ch, i) => {
      const out = outputByKey.get(`chapter:${ch.id}`);
      const words = chapterWords.get(ch.id) ?? 0;
      const isRendering = rendering && (activeChapter === ch.id || (activeChapterTitle != null && ch.title === activeChapterTitle));
      let status: ChapterStatus;
      if (isRendering) status = "rendering";
      else if (out) status = "rendered";
      else if (bookRendering) status = "pending";
      else status = "not";
      return {
        ...ch, number: i + 1, words,
        durationMs: out?.durationMs ?? null, renderedAt: out?.createdAt ?? null,
        estMs: estMsFromWords(words), status, isRendering
      };
    });
  }, [state?.chapters, chapterPos, chapterWords, outputByKey, rendering, activeChapter, bookRendering, activeChapterTitle]);

  const counts = useMemo(() => {
    const c = { all: chapters.length, rendered: 0, rendering: 0, pending: 0, not: 0 };
    for (const ch of chapters) c[ch.status]++;
    return c;
  }, [chapters]);

  const totalWords = useMemo(() => chapters.reduce((s, c) => s + c.words, 0), [chapters]);
  const totalMs = useMemo(() => chapters.reduce((s, c) => s + (c.durationMs ?? c.estMs), 0), [chapters]);

  const visible = chapters.filter((c) =>
    filter === "all" ? true : filter === "pending" ? (c.status === "pending" || c.status === "rendering") : c.status === filter);

  const recent = useMemo(() => {
    const items = chapters
      .filter((c) => c.renderedAt)
      .map((c) => ({ number: c.number, title: c.title, when: c.renderedAt!, durationMs: c.durationMs, status: "rendered" as const }))
      .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
    const active = chapters.find((c) => c.isRendering);
    const head = active ? [{ number: active.number, title: active.title, when: "", durationMs: active.estMs, status: "rendering" as const }] : [];
    return [...head, ...items].slice(0, 5);
  }, [chapters]);

  if (available === false) {
    return (
      <div className="ab-scroll"><div className="ab-inner">
        <AudiobookHero />
        <div className="rel-empty" style={{ marginTop: 20 }}>
          <Icon name="volume" size={26} style={{ color: "var(--text-faint)" }} />
          <p className="muted">No narration provider is reachable. Audiobook needs a server-side voice (Blueprint / local Chatterbox, or OpenAI).</p>
        </div>
      </div></div>
    );
  }

  const voiceHint = "Server-side narrator voice. Warm, natural narration suited to long-form fiction.";

  return (
    <div className="ab-scroll">
      <div className="ab-inner">
        <AudiobookHero />

        {/* Narration controls */}
        <section className="card ab-controls">
          <div className="ab-ctrl-voice">
            <span className="ab-ctrl-ico"><Icon name="volume" size={18} /></span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <label className="label" style={{ marginBottom: 4 }}>Narrator voice</label>
              <div className="row gap-2">
                {voices.length > 1 ? (
                  <select className="select" value={voice} onChange={(e) => setVoice(e.target.value)} disabled={rendering} style={{ minWidth: 160 }}>
                    {voices.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                ) : (
                  <div className="strong" style={{ padding: "8px 0" }}>{voice || "Default"}</div>
                )}
                <button className="btn btn-icon btn-ghost ab-preview" title="Preview voice" onClick={previewVoice}>
                  <Icon name={previewing ? "stop" : "play"} size={14} />
                </button>
              </div>
              <p className="faint ab-voice-hint">{voiceHint}</p>
            </div>
          </div>

          <div className="ab-ctrl-main">
            {!bookRendering ? (
              <button className="btn btn-primary ab-render-book" onClick={() => start("book")} disabled={rendering}>
                <Icon name="volume" size={16} /> {bookDuration != null ? "Re-render whole book" : "Render Whole Book"}
              </button>
            ) : (
              <button className="btn ab-render-book" onClick={cancel}>
                <Icon name="stop" size={15} /> Stop rendering
              </button>
            )}
            <span className="faint ab-render-sub">
              {bookRendering ? `Rendering ${done}/${total} clips · ${pct}%` : "Queue all chapters for background rendering"}
            </span>
            {bookRendering && (
              <div className="ab-book-progress"><div className="ab-book-fill" style={{ width: `${pct}%` }} /></div>
            )}
          </div>

          <button className="ab-ctrl-settings" onClick={() => setSettingsOpen((o) => !o)} aria-expanded={settingsOpen}>
            <span className="ab-ctrl-ico"><Icon name="settings" size={18} /></span>
            <div style={{ textAlign: "left" }}>
              <div className="strong" style={{ fontSize: 13.5 }}>Narration Settings</div>
              <div className="faint" style={{ fontSize: 11.5 }}>Voice, speed, formatting, and more</div>
            </div>
            <Icon name={settingsOpen ? "chevronUp" : "chevronDown"} size={15} className="faint" />
          </button>
        </section>

        {settingsOpen && (
          <section className="card pad ab-settings">
            <div className="ab-settings-grid">
              <div className="field" style={{ margin: 0 }}>
                <label className="label">Narrator voice</label>
                {voices.length > 1 ? (
                  <select className="select" value={voice} onChange={(e) => setVoice(e.target.value)} disabled={rendering}>
                    {voices.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                ) : <div className="strong">{voice || "Default"}</div>}
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label className="label">Rendering model</label>
                <div className="faint" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  The book renders one file per chapter — listen to or download each from its row below.
                </div>
              </div>
            </div>
            <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>
              “Render Whole Book” queues every chapter that isn't rendered yet; each is saved as it finishes, so you can stop any time and already-rendered chapters are kept. Speed, formatting, and scene-break handling are configured server-side. Local narration is slow (~15s per clip); you can leave this page while it renders.
            </p>
            <div className="ab-danger-zone">
              <div style={{ minWidth: 0 }}>
                <div className="strong" style={{ fontSize: 13 }}>Delete all renders</div>
                <div className="faint" style={{ fontSize: 11.5 }}>
                  Removes every rendered chapter and cached clip so the whole book renders fresh. Your manuscript isn't touched.
                </div>
              </div>
              <button className="btn btn-sm btn-danger" onClick={clearAll} disabled={rendering || (state?.outputs ?? []).length === 0}>
                <Icon name="trash" size={13} /> Delete all renders
              </button>
            </div>
          </section>
        )}

        {player && (
          <AudiobookPlayer
            projectId={projectId}
            scope={player.scope}
            chapterId={player.chapterId}
            voice={voice}
            title={player.title}
            onClose={() => setPlayer(null)}
          />
        )}

        {!rendering && render?.status === "error" && (
          <div className="ab-error">
            <Icon name="alert" size={15} style={{ color: "var(--danger, #f85149)" }} /> {render.error ?? "Render failed."}
          </div>
        )}

        {/* Main grid: chapters + sidebar */}
        <div className="ab-grid">
          <div className="ab-main">
            <div className="ab-chapters-head">
              <div>
                <h3 style={{ margin: 0 }}>Chapters</h3>
                <p className="faint" style={{ fontSize: 12, margin: "2px 0 0" }}>
                  {chapters.length} chapters · {formatNumber(totalWords)} words · estimated {formatDuration(totalMs)}
                </p>
              </div>
              <div className="ab-filters">
                <FilterPill label="All" count={counts.all} active={filter === "all"} onClick={() => setFilter("all")} />
                <FilterPill label="Rendered" count={counts.rendered} active={filter === "rendered"} onClick={() => setFilter("rendered")} />
                <FilterPill label="Pending" count={counts.pending + counts.rendering} active={filter === "pending"} onClick={() => setFilter("pending")} />
                <FilterPill label="Not rendered" count={counts.not} active={filter === "not"} onClick={() => setFilter("not")} />
              </div>
            </div>

            <div className="ab-chapters">
              {visible.length === 0 ? (
                <div className="ab-empty-row faint">No chapters in this view.</div>
              ) : visible.map((ch) => (
                <div key={ch.id} className={`ab-chapter status-${ch.status}`}>
                  <div className="ab-ch-num">{String(ch.number).padStart(2, "0")}</div>
                  <div className="ab-ch-info">
                    <div className="ab-ch-title truncate">{ch.title}</div>
                    <div className="ab-ch-meta">{formatNumber(ch.words)} words · ~{formatDuration(ch.durationMs ?? ch.estMs)}</div>
                  </div>

                  <div className="ab-ch-status">
                    {ch.status === "rendered" && (
                      <>
                        <button className="ab-ch-play" title="Listen" onClick={() => setPlayer({ scope: "chapter", chapterId: ch.id, title: ch.title })}>
                          <Icon name="play" size={14} />
                        </button>
                        <span className="ab-wave" aria-hidden>{WAVE_BARS}</span>
                        <span className="ab-ch-dur faint">{formatDuration(ch.durationMs ?? 0)}</span>
                      </>
                    )}
                    {ch.status === "rendering" && (
                      <div className="ab-ch-rendering">
                        <span className="ab-status-label rendering"><Icon name="volume" size={13} className="spin-slow" /> Rendering · {pct}%</span>
                        <div className="ab-ch-bar"><div style={{ width: `${pct}%` }} /></div>
                      </div>
                    )}
                    {ch.status === "pending" && <span className="ab-status-label pending"><span className="ab-dot" /> Pending</span>}
                    {ch.status === "not" && <span className="ab-status-label not"><span className="ab-dot" /> Not rendered</span>}
                  </div>

                  <div className="ab-ch-actions">
                    {ch.isRendering ? (
                      <button className="btn btn-sm" onClick={cancel}><Icon name="x" size={13} /> Cancel</button>
                    ) : (
                      <button className="btn btn-sm btn-primary" onClick={() => start("chapter", ch.id)} disabled={rendering}>
                        <Icon name="volume" size={13} /> {ch.status === "rendered" ? "Re-render" : "Render"}
                      </button>
                    )}
                    <button className="btn btn-icon btn-ghost btn-sm" title="More"
                      onClick={(e) => openMenu(e, [
                        ...(ch.status === "rendered" ? [
                          { label: "Listen", icon: "play" as const, onClick: () => setPlayer({ scope: "chapter", chapterId: ch.id, title: ch.title }) },
                          { label: "Download chapter", icon: "download" as const, onClick: () => void download("chapter", ch.id) },
                          { label: "Re-render", icon: "volume" as const, onClick: () => void start("chapter", ch.id) }
                        ] : [
                          { label: "Render", icon: "volume" as const, onClick: () => void start("chapter", ch.id) }
                        ])
                      ])}>
                      <Icon name="dots" size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="ab-side">
            <section className="card pad">
              <h3 className="ab-side-title">Audiobook Progress</h3>
              <div className="ab-progress-row">
                <ProgressRing counts={counts} total={chapters.length} />
                <div className="ab-progress-stats">
                  <div className="ab-stat-label faint">Estimated Length</div>
                  <div className="ab-stat-big">{formatDuration(totalMs)}</div>
                  <div className="faint" style={{ fontSize: 12 }}>{formatNumber(totalWords)} words</div>
                </div>
              </div>
              <div className="ab-legend">
                <LegendRow color="var(--accent)" label="Rendered" n={counts.rendered} />
                <LegendRow color="#a78bfa" label="Rendering" n={counts.rendering} />
                <LegendRow color="#5b8def" label="Pending" n={counts.pending} />
                <LegendRow color="var(--text-faint)" label="Not rendered" n={counts.not} />
              </div>
            </section>

            <section className="card pad">
              <div className="row" style={{ alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <h3 className="ab-side-title" style={{ margin: 0 }}>Recent Activity</h3>
                {recent.length > 0 && <button className="btn btn-sm btn-ghost" onClick={() => setFilter("rendered")}>View all</button>}
              </div>
              {recent.length === 0 ? (
                <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>No renders yet. Render a chapter to see activity here.</p>
              ) : (
                <div className="ab-activity">
                  {recent.map((r, i) => (
                    <div key={i} className="ab-activity-row">
                      <Icon name={r.status === "rendering" ? "volume" : "check"} size={15}
                        className={r.status === "rendering" ? "spin-slow" : ""}
                        style={{ color: r.status === "rendering" ? "#a78bfa" : "var(--accent)", flex: "none" }} />
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="truncate" style={{ fontSize: 12.5 }}>
                          <span className="strong">Chapter {r.number}</span> — {r.title}
                        </div>
                        <div className="faint" style={{ fontSize: 11 }}>
                          {r.status === "rendering" ? `Rendering · ${pct}% · ~${formatDuration(r.durationMs ?? 0)}` : `Rendered · ${formatWhen(r.when)} · ${formatDuration(r.durationMs ?? 0)}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="card pad ab-tips">
              <div className="row gap-2" style={{ marginBottom: 8 }}>
                <Icon name="sparkles" size={15} style={{ color: "#f5c451" }} />
                <h3 className="ab-side-title" style={{ margin: 0 }}>Tips</h3>
              </div>
              <ul>
                <li>You can keep writing while chapters render in the background.</li>
                <li>Only scenes that changed will be re-rendered.</li>
                <li>Use consistent scene formatting for the best results.</li>
                <li>Adjust narration settings for tone, pacing, and scene breaks.</li>
              </ul>
            </section>
          </aside>
        </div>
      </div>
      {menu}
    </div>
  );
}

const WAVE_BARS = Array.from({ length: 28 }).map((_, i) => (
  <span key={i} style={{ height: `${20 + Math.abs(Math.sin(i * 1.3)) * 70}%` }} />
));

function AudiobookHero() {
  return (
    <div className="ab-hero">
      <div className="ab-hero-glow" />
      <span className="ab-hero-wave" aria-hidden>
        {Array.from({ length: 72 }).map((_, i) => (
          <span key={i} style={{ height: `${10 + Math.abs(Math.sin(i * 0.5)) * 90}%` }} />
        ))}
      </span>
      <div className="ab-hero-body">
        <h1 className="ab-hero-title">Audiobook</h1>
        <p className="ab-hero-sub">
          Render narrated audio in the background — the whole book, or a chapter at a time.
          <br />Scenes are cached, so edits only re-render what changed.
        </p>
      </div>
    </div>
  );
}

function FilterPill({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button className={`ab-filter ${active ? "active" : ""}`} onClick={onClick}>
      {label} <span className="ab-filter-n">{count}</span>
    </button>
  );
}

function LegendRow({ color, label, n }: { color: string; label: string; n: number }) {
  return (
    <div className="ab-legend-row">
      <span className="ab-legend-dot" style={{ background: color }} />
      <span className="ab-legend-label">{label}</span>
      <span className="ab-legend-n">{n}</span>
    </div>
  );
}

function ProgressRing({ counts, total }: { counts: { rendered: number; rendering: number; pending: number; not: number }; total: number }) {
  const t = Math.max(1, total);
  const pctRendered = (counts.rendered / t) * 100;
  const pctRendering = (counts.rendering / t) * 100;
  const pctPending = (counts.pending / t) * 100;
  const s1 = pctRendered, s2 = s1 + pctRendering, s3 = s2 + pctPending;
  const bg = `conic-gradient(var(--accent) 0 ${s1}%, #a78bfa ${s1}% ${s2}%, #5b8def ${s2}% ${s3}%, color-mix(in srgb, var(--text-faint) 35%, transparent) ${s3}% 100%)`;
  return (
    <div className="ab-ring" style={{ background: bg }}>
      <div className="ab-ring-hole">
        <div className="ab-ring-num">{counts.rendered} / {total}</div>
        <div className="ab-ring-label">chapters</div>
      </div>
    </div>
  );
}
